<script setup lang="ts">
import { computed } from 'vue'

import { OutlinedDownIcon } from '@/components/icons'
import { getImageUrl } from '@/components/common/common'

export type Product = {
  title: string
  subTitle?: string
  bannerText?: string
  bannerSubTextBold?: string
  bannerSubTextRegular?: string
  logo?: any
  logoProps?: {
    [key: string]: string | number
  }
  description: string
  productImage: string
  orientation?: 'row' | 'column'
}

export interface IProps {
  data: Product
  first: boolean
  last: boolean
}

const props = defineProps<IProps>()

const logoProps = computed(() => props.data.logoProps)
</script>

<template>
  <div
    class="offer-product-outer"
    :class="{
      row: props.data.orientation !== 'column',
      column: props.data.orientation === 'column',
      first: first,
      last: last
    }"
  >
    <div class="title-container">
      <span class="title">{{ props.data.title }}</span>
      <span class="sub-title">{{ props.data.subTitle }}</span>
    </div>
    <div class="banner-container">
      <span class="banner">{{ props.data.bannerText }}</span>
      <span class="sub-banner-bold">{{ props.data.bannerSubTextBold }}</span>
      <div v-if="props.data.bannerSubTextRegular" class="sub-banner-regular">
        <span>{{ props.data.bannerSubTextRegular }}</span>
        <div class="next-icon"><OutlinedDownIcon /></div>
      </div>
    </div>
    <div v-if="props.data.logo" class="logo-container">
      <component :is="props.data.logo" v-bind="logoProps" />
    </div>
    <div class="description-container">
      <span>{{ props.data.description }}</span>
    </div>
    <div class="product-container"><img :src="getImageUrl(props.data.productImage)" /></div>
  </div>
</template>

<style scoped>
.offer-product-outer {
  display: grid;
  gap: 2.5%;
  padding-inline: 5%;
  padding-top: 15%;
  padding-bottom: 5%;
  height: 100%;
}

.offer-product-outer.first {
  padding-left: 20%;
}

.offer-product-outer.last {
  padding-right: 15%;
}

.offer-product-outer {
  display: grid;
  gap: 2.5%;
}

.offer-product-outer.row {
  grid-template-rows: 0.2fr 0.4fr 0.4fr;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-areas:
    'title title logo'
    'banner description description'
    'product product product';
}

.offer-product-outer.column {
  grid-template-rows: 0.2fr 0.4fr 0.4fr;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    'title logo'
    'banner product'
    'description product';
}

.title-container {
  grid-area: title;
  display: flex;
  flex-direction: column;
  text-align: start;
  justify-content: center;
}

.title-container .title {
  font-size: var(--font-size-h1);
  line-height: var(--font-size-h1);
  font-weight: 700;
}

.title-container .sub-title {
  font-size: var(--font-size-h3);
}

.banner-container {
  grid-area: banner;
  display: flex;
  flex-direction: column;
  align-items: start;
  font-size: var(--font-size-h1);
  line-height: var(--font-title-2);
  text-align: start;
}

.banner-container .banner {
  font-size: var(--dollar-value-large);
  line-height: var(--dollar-value-large);
  font-weight: 700;
}

.banner-container .sub-banner-bold {
  font-weight: 700;
}

.banner-container .sub-banner-regular {
  display: flex;
  align-items: center;
  gap: 0.3125rem;
}

.banner-container .sub-banner-regular .next-icon {
  transform: rotate(-90deg);
}

.banner-container .banner,
.banner-container .sub-banner-bold {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 10rem;
}

.logo-container {
  grid-area: logo;
}

.description-container {
  grid-area: description;
  font-size: var(--font-size-small);
  text-align: start;
}

.offer-product-outer .product-container {
  grid-area: product;
  display: flex;
  align-items: end;
}

.offer-product-outer.last .product-container {
  margin-right: 10%;
}

.offer-product-outer.row .product-container {
  max-height: 7.8125rem;
}

.product-container img {
  height: 100%;
  width: 100%;
  object-fit: contain;
  min-height: 12.5rem;
}

.offer-product-outer.row .product-container img {
  min-height: 7.8125rem;
  transform: scale(0.8);
}
</style>
